import {Select} from 'components/select/Select'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import {NavigationContext} from 'modules/dashboard/NavigationContext'
import {getDefaultBrowserConfig, getDefaultOperationalHours, getDefaultOptions} from './constants'
import {connectService} from 'components/service/connectService'
import {DeviceTypes, DeviceStatus as Status, scrollToTop} from 'utility/utility'
import {useConfirm} from 'hooks/useConfirm'
import {chunk, has, isArray, isNull, isObject, isString, isUndefined, merge, omitBy, orderBy} from 'lodash'
import {ActivationCode, DeviceGroups, OperationalHours, Options, PhysicalSiteContacts} from './components'
import {useNotification} from 'hooks/useNotification'
import {useFormik} from 'formik'
import {DevicePrototype, DeviceSchema} from './schema'
import {ValidationError} from 'components/validation-error/ValidationError'
import {RemoteRestart} from './components/RemoteRestart'
import {DeviceStatus} from './components/DeviceStatus'
import {DigitalSignage} from './components/DigitalSignange'
import {BrowserOptions} from './components/BrowserOptions'
import {AutoComplete} from 'components/auto-complete/AutoComplete'
import feathers from 'utility/client'
import moment from 'moment'
import { useSelector } from 'react-redux'

export const Device = connectService(props => {
  const confirm = useConfirm()
  const navigate = useNavigate()
  const notify = useNotification()
  const activationRef = useRef(null)
  const {business, reseller, distributor, user, device} = useParams()
  const {setTitle} = useContext(NavigationContext)
  const state = useSelector(state => state.state)

  const [businessEntity, setBusinessEntity] = useState({})

  const {data, setData, onSave, remove, office = false, update = false} = props
  const [createCity, setCreateCity] = useState(false)

  const [requestingNewAccessCode, setRequestingNewAccessCode] = useState(false)

  useEffect(() => {
    scrollToTop()

    setTitle(`${device ? 'Update' : 'Create'} ${office ? 'Office' : 'Remote'} Device`)

    if (user) {
      formik.setFieldValue('user', user)
    }
  }, [])

  const beforeSave = payload => {
    delete payload._id
    delete payload.__v

    payload.type = office ? DeviceTypes.OFFICE : DeviceTypes.REMOTE
    payload.business = business

    if (reseller) {
      payload.reseller = reseller
    }

    if (distributor) {
      payload.distributor = distributor
    }

    if (user) {
      payload.user = user
    }

    payload.options = {
      ...getDefaultOptions(office),
      ...payload.options
    }

    if (!payload.operationalHours.length) {
      payload.operationalHours = getDefaultOperationalHours(office)
    }

    return omitBy(payload, i => isNull(i) || isUndefined(i) || (isString(i) && !i))
  }

  const onRemove = device => {
    confirm({
      title: `Confirm Deletion ${office ? 'Office' : 'User'} Device`,
      message: `Are you sure you want to remove device? Once removed, device will be disconnected from Video Window apps!`,
      onConfirm: async () => {
        await remove(device)
        navigate(-1)
      }
    })
  }

  const onRequestAccessCode = () => {
    setRequestingNewAccessCode(true)
    feathers.io.emit('access', 'device', {device: data._id}, {}, (e, response) => {
      setRequestingNewAccessCode(false)
      if (e) {
        notify({
          notification: e.message,
          variant: 'danger'
        })

        return
      }

      activationRef.current.show({
        code: chunk(response.activationCode.split(''), 4)
          .map(c => c.join(''))
          .join('-'),
        validity: moment(response.activationValidTill).format('DD MMMM YYYY')
      })
    })
  }

  const onEmailAccessCode = email => {
    feathers.io.emit('mail', 'device', {device: data._id, email}, {}, (e, data) => {
      if (e) {
        notify({
          notification: e.message,
          variant: 'danger'
        })

        return
      }

      notify({
        notification: 'Access code has been sent!',
        variant: 'primary'
      })
    })
  }

  const onDeviceRestarted = () => {
    setData({...data, restartedAt: moment().toISOString()})
  }

  const initialValues = useMemo(() => {
    const _data = {...data}
    if (_data.DigitalSignage && isObject(_data.digitalSignage)) {
      _data.showDigitalSignage = true
    }

    if (_data.browserOptions && isArray(_data.browserOptions)) {
      _data.allowBrowser = true
    }

    return update
      ? merge({}, DevicePrototype(office, business, user), _data)
      : {...DevicePrototype(office, business, user)}
  }, [update, data])

  useEffect(() => {
    feathers.service('business').get(business).then(b => setBusinessEntity(b)).catch(console.error)
  }, [business])

  const formik = useFormik({
    initialValues,
    validationSchema: DeviceSchema,
    onSubmit: async payload => {
      console.log('Saving device', beforeSave(payload))
      const result = await onSave({data: payload, beforeSave})
      setData(result)

      if (office && !update) {
        activationRef.current.show({
          code: chunk(result.activationCode.split(''), 4)
            .map(c => c.join(''))
            .join('-'),
          validity: moment(result.activationValidTill).format('DD MMMM YYYY')
        })
      } else {
        navigate(-1)
      }
    },
    enableReinitialize: true
  })

  return (
    <>
      <DeviceStatus
        device={data?._id}
        className={!office ? 'mb-4' : ''}
        state={state?.[data?._id] ? Status.Live : Status.Offline}
        restartedAt={data?.restartedAt}
      />
      {office && (
        <div>
          <div className="form-group">
            <label className="col-form-label">Device name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter device name"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              autoFocus
            />
            <ValidationError formik={formik} property="name" />
          </div>
          <hr />
        </div>
      )}
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">
          <label className="col-form-label">Device group</label>
        </label>
        <div className="col-sm-8 col-lg-7">
          <DeviceGroups
            query={{business}}
            service="group"
            groups={formik.values.groups}
            onChange={groups => formik.setFieldValue('groups', groups)}
          />
          <ValidationError formik={formik} property="groups" />
        </div>
      </div>

      {office && update && (
        <div className="row">
          <div className="col-sm-8 col-lg-7 offset-sm-4 offset-lg-5 mt-3">
            <div className="row mt-3">
              <div className="col-sm-12 col-lg-6">
                <button
                  className="btn btn-primary btn-block"
                  onClick={requestingNewAccessCode ? null : onRequestAccessCode}
                >
                  {requestingNewAccessCode ? (
                    <div className="spinner-border spinner-border-sm text-light"></div>
                  ) : (
                    <i className="fal fa-fingerprint mr-2"></i>
                  )}
                  {requestingNewAccessCode ? 'Generating new Activation Code' : 'Re-generate Activation Code'}
                </button>
              </div>

              <div className="col-sm-12 mt-lg-0 mt-sm-2 col-lg-6">
                <RemoteRestart device={data._id} onRestart={onDeviceRestarted} />
              </div>
            </div>
          </div>
        </div>
      )}

      {!office && update && (
        <div className="row">
          <div className="col-sm-8 col-lg-7 offset-sm-4 offset-lg-5 mt-3">
            <RemoteRestart device={data._id} onRestart={onDeviceRestarted} />
          </div>
        </div>
      )}

      <hr />
      <h4 className="mt-3">Details</h4>
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Country</label>
        <div className="col-sm-8 col-lg-7">
          <Select
            service="country"
            title="Select Country"
            InputProps={{autoFocus: true}}
            value={formik.values.country}
            onChange={e => formik.setFieldValue('country', e.target.value)}
            query={{$sort: {name: 1}}}
          />
          <ValidationError formik={formik} property="country" />
        </div>
      </div>

      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">City/Town</label>
        <div className="col-sm-8 col-lg-7">
          <div className="row justify-content-between align-items-center">
            {
              createCity ? (
                <div className="col-sm-8 col-lg-7">
                  <input
                    placeholder="City/Town"
                    className="form-control"
                    name="city"
                    value={formik.values.city}
                    onChange={formik.handleChange}
                  />
                  <ValidationError formik={formik} property="city" />
                </div>
              ) : (
                <div className="col-sm-8">
                  <AutoComplete
                    value={formik.values.city}
                    service="city" inputProps={{ placeholder: 'Type to search city' }}
                    onChange={(city) => {
                      formik.setFieldValue('city', city._id)
                    }}
                  />
                  <ValidationError formik={formik} property="city" />
                </div>
              )
            }
            <div className="col-sm-1 text-muted text-sm">
              <small>OR</small>
            </div>
            <div className="col-sm-3">
              <button className="btn btn-primary" onClick={() => {
                formik.setFieldValue('city', '')
                setCreateCity(!createCity)
              }}>
                {
                  createCity ? 'Select existing' : (
                    <span>
                      <i className="fal fa-plus"></i> Create City
                    </span>
                  )
                }
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Floor (optional)</label>
        <div className="col-sm-8 col-lg-7">
          <input
            type="text"
            className="form-control"
            name="floor"
            value={formik.values.floor}
            onChange={formik.handleChange}
          />
          <ValidationError formik={formik} property="floor" />
        </div>
      </div>
      <div className="form-group row">
        <label className="col-sm-4 col-lg-5 col-form-label">Nickname (optional)</label>
        <div className="col-sm-8 col-lg-7">
          <input
            type="text"
            className="form-control"
            name="nickname"
            value={formik.values.nickname}
            onChange={formik.handleChange}
          />
          <ValidationError formik={formik} property="nickname" />
        </div>
      </div>
      <hr />
      <h4>Hours of operation</h4>
      <div className="form-group row">
        <label className="col-sm-6 col-lg-4 col-form-label">Timezone</label>
        <div className="col-sm-6 col-lg-4">
          <Select
            service="timezone"
            title="Select Timezone"
            textProperty="timezone"
            InputProps={{autoFocus: true}}
            value={formik.values.timezone}
            onChange={e => formik.setFieldValue('timezone', e.target.value)}
            query={{disablePagination: true}}
            onResult={(data) => orderBy(data, ['timezone'], ['asc'])}
          />
          <ValidationError formik={formik} property="timezone" />
        </div>
      </div>

      <OperationalHours
        type={office ? DeviceTypes.OFFICE : DeviceTypes.REMOTE}
        operationalHours={formik.values.operationalHours}
        onChange={hours => formik.setFieldValue('operationalHours', hours)}
      />

      <hr />

      <Options
        type={office ? DeviceTypes.OFFICE : DeviceTypes.REMOTE}
        options={formik.values.options}
        business={businessEntity}
        onChange={options => formik.setFieldValue('options', options)}
      />

      <hr />

      <BrowserOptions
        disabled={has(businessEntity?.options, 'browserAccess') ? !businessEntity?.options?.browserAccess : false}
        allowBrowser={formik.values.allowBrowser}
        browserOptions={formik.values.browserOptions}
        type={office ? DeviceTypes.OFFICE : DeviceTypes.REMOTE}
        onAllowBrowserChange={allowed => {
          formik.setFieldValue('browserOptions', allowed ? [getDefaultBrowserConfig()] : [])
          formik.setFieldValue('allowBrowser', allowed)
        }}
        onOptionsChange={options => formik.setFieldValue('browserOptions', options)}
        edit={update}
      />

      <hr />

      <DigitalSignage
        disabled={has(businessEntity?.options, 'digitalSignageAccess') ? !businessEntity?.options?.digitalSignageAccess : false}
        showDigitalSignage={formik.values.showDigitalSignage}
        digitalSignage={formik.values.digitalSignage}
        onShowSignageChange={show => formik.setFieldValue('showDigitalSignage', show)}
        onOptionsChange={options => formik.setFieldValue('digitalSignage', options)}
      />

      <hr />

      <PhysicalSiteContacts
        siteContacts={formik.values.siteContacts}
        onChange={contacts => formik.setFieldValue('siteContacts', contacts)}
      />

      <div className="vw-popup-box__inner__body__btns">
        <button className="btn btn-primary" type="button" onClick={formik.handleSubmit}>
          Save {office ? 'Office Device' : 'User Device'}
        </button>

        {update && (
          <button className="btn btn-danger" type="button" onClick={() => onRemove(data)}>
            Remove {office ? 'Office Device' : 'User Device'}
          </button>
        )}
        <button className="btn btn-secondary">Cancel</button>
      </div>

      <ActivationCode ref={activationRef} onClose={() => navigate(-1)} onEmailRequest={onEmailAccessCode} />
    </>
  )
}, 'device', {
  query: {
    $populate: {
      path: 'groups',
      populate: {
        path: '_id',
        model: 'group'
      }
    },
  },
  paramsAsQueryParams: ['distributor', 'reseller', 'business']
})
